import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/hooks/useAuth";
import { ApiClientError, getErrorMessage, isMfaChallenge } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Alert } from "@/components/ui/Alert";
import { Eye, EyeSlash as EyeOff } from "@phosphor-icons/react";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const mfaCodeSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app"),
});

type MfaCodeFormData = z.infer<typeof mfaCodeSchema>;

/**
 * Admin login page.
 *
 * A trimmed copy of packages/frontend/src/pages/auth/LoginPage.tsx: email +
 * password, then the authenticator-app (TOTP) challenge when the account has
 * MFA enabled (see #163). No Google sign-in, no "create an account", no
 * unverified-email resend flow — admin accounts are provisioned internally,
 * they don't self-register, so none of that applies here.
 */
export function LoginPage() {
  const { login, respondMfa } = useAuth();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const sessionExpired = searchParams.get("session") === "expired";
  // Set by the global ADMIN_MFA_REQUIRED handler (app/providers.tsx) when an
  // admin API call comes back 403 with no fresh TOTP proof — either this
  // admin's session outlived its MFA proof (token refresh, or enforcement
  // just turned on) or, in principle, an unenrolled admin reached an admin
  // route some other way. Either way the fix is the same form below, not a
  // generic error screen.
  const mfaRequired = searchParams.get("reason") === "mfa_required";
  // Set by useAuth's completeSignIn (valid credentials, but the account has
  // no platform_* Cognito group) and by AdminGuard (a direct visit with a
  // signed-in, non-admin session already in this tab). Both already signed
  // this browser out of the admin app before landing here, so this is
  // purely explaining why, not an error to retry.
  const noRole = searchParams.get("reason") === "no_role";
  // The email rides in router state, not the query string — a query param
  // would land in Cloudflare/CDN access logs, browser history and any
  // Referer header the next navigation sends. State is in-memory only, so
  // it's simply absent after a reload (a direct AdminGuard redirect never
  // sets it either); either way the message below degrades to email-less.
  const noRoleEmail = (location.state as { email?: string } | null)?.email;

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({ resolver: zodResolver(loginSchema) });

  const {
    register: registerMfaCode,
    handleSubmit: handleMfaSubmit,
    formState: { errors: mfaErrors },
  } = useForm<MfaCodeFormData>({ resolver: zodResolver(mfaCodeSchema) });

  const challenge =
    login.data && isMfaChallenge(login.data) ? login.data : undefined;

  const onSubmitMfaCode = (data: MfaCodeFormData) => {
    if (!challenge) return;
    respondMfa.mutate({
      session: challenge.session,
      email: getValues("email"),
      code: data.code,
    });
  };

  const onSubmit = (data: LoginFormData) => {
    login.mutate(data);
  };

  // Every hook above this point runs on every render regardless of which
  // branch renders — see the frontend LoginPage's identical comment for why
  // that matters here (challenge flips between renders of the same
  // instance).
  if (challenge) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold tracking-tight">
            Enter your authenticator code
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {challenge.message}
          </p>
        </div>

        {respondMfa.error && (
          <Alert variant="error">
            {getErrorMessage(
              respondMfa.error,
              "Could not verify that code. Please try again."
            )}
          </Alert>
        )}

        <form onSubmit={handleMfaSubmit(onSubmitMfaCode)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mfaCode">6-digit code</Label>
            <Input
              id="mfaCode"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              error={mfaErrors.code?.message}
              {...registerMfaCode("code")}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            loading={respondMfa.isPending}
          >
            Verify
          </Button>

          <button
            type="button"
            onClick={() => login.reset()}
            className="w-full text-sm text-muted-foreground hover:text-foreground underline"
          >
            Use a different account
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight">Admin sign in</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign in with your platform admin account
        </p>
      </div>

      {sessionExpired && (
        <Alert variant="warning">
          Your session has expired. Please sign in again.
        </Alert>
      )}

      {mfaRequired && !sessionExpired && (
        <Alert variant="warning">
          Admin access requires a second factor. Sign in again with your
          authenticator code to continue.
        </Alert>
      )}

      {noRole && !sessionExpired && !mfaRequired && (
        <Alert variant="warning">
          {noRoleEmail ? `Signed in as ${noRoleEmail}, but this` : "This"}{" "}
          account doesn't have admin access. Ask a platform admin to add you
          to an admin group, or sign in with a different account.
        </Alert>
      )}

      {login.error && (
        <Alert variant="error">
          {getErrorMessage(
            login.error,
            login.error instanceof ApiClientError &&
              login.error.code === "AUTH_INVALID_CREDENTIALS"
              ? "Incorrect email or password."
              : "Login failed. Please check your credentials."
          )}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@charisol.io"
            autoComplete="email"
            error={errors.email?.message}
            {...register("email")}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              autoComplete="current-password"
              error={errors.password?.message}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <Button type="submit" className="w-full" loading={login.isPending}>
          Sign In
        </Button>
      </form>
    </div>
  );
}
